import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'

import {
  Box,
  Button,
  Columns,
  LoadingModule,
  PageHeader,
} from '@domain-group/suit-theme'

import { useNumericParams } from '@domain-group/react-hooks'

import PendingInvoices from '@components/PendingInvoices'

import PageContext from '@contexts/pageContext'

import useAgencySelector from '@hooks/useAgencySelector'
import useCampaignAccounting from '@hooks/useCampaignAccounting'
import useInvoice from '@hooks/useInvoice'

import { CampaignModel } from '@domain-group/realhub-backend-api-types'

import tabBarItems from '@sections/Client/packs/Campaign/utils/generateQuoteAndFinanceTabBarItems'

import InvoiceInfoPanel from '../InvoiceInfoPanel'
import InvoiceEditPanel from '../InvoiceEditPanel'

interface PageContentProps {
  campaign: CampaignModel,
  pageFilters: {},
}

const PageContent = (props: PageContentProps) => {
  const { campaign, pageFilters } = props
  const { selectedAgencyId } = useAgencySelector()
  const { invoiceId } = useNumericParams()

  const useInvoicePayload = useInvoice({ id: invoiceId })

  const {
    callbacks: {
      approveInvoice,
      deleteInvoice,
    },
    invoice,
    loading,
    urls: {
      pdfDownloadUrl,
    },
  } = useInvoicePayload

  const pageContext = useContext(PageContext)

  const {
    callbacks: {
      showAddAccountingItemModal,
      showCreateOrEditInvoiceModal,
    },
  } = pageContext

  const campaignId = campaign.id

  const accountingPayload = useCampaignAccounting({
    agencyId: selectedAgencyId,
    campaignId,
    filters: { ...pageFilters },
    performHttpRequests: !!campaignId && !!selectedAgencyId,
  })

  return (
    <>
      <Helmet>
        <title>Accounting | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button
            buttonStyle="primaryCreate"
            onClick={() => showAddAccountingItemModal({ campaign })}
            size="medium"
          >
            Create Item
          </Button>
        )}
        activeTabBarItemKey="invoices"
        boxProps={{ height: [112, 105], justifyContent: 'flex-start' }}
        nestedNavigation
        tabBarItems={tabBarItems(campaignId, invoiceId)}
        title="Accounting"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns style={{ marginTop: 16 }}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && (
              <>
                <InvoiceInfoPanel invoice={invoice} />

                <InvoiceEditPanel
                  callbacks={{
                    approveInvoice,
                    deleteInvoice,
                    showCreateOrEditInvoiceModal,
                  }}
                  campaign={campaign}
                  invoice={invoice}
                  pdfDownloadUrl={pdfDownloadUrl}
                />
              </>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PendingInvoices
              accountingPayload={accountingPayload}
              campaign={campaign}
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
